import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
    BookOpen, 
    Download, 
    Sparkles, 
    Mail, 
    ArrowRight, 
    ShieldCheck, 
    Calendar,
    ChevronRight,
    Loader2,
    CheckCircle2,
    AlertCircle,
    FileText
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const getApiUrl = () => {
    if (typeof window === 'undefined') return 'http://localhost:8001/api';
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://localhost:8001/api';
    }
    return `${window.location.origin}/api`;
};
const API = getApiUrl();

const FALLBACK_ISSUES = [
    {
        issue: 6,
        title: "The Neural Feed Illustrated — Issue #06",
        period_label: "5–11 October 2026",
        page_count: 11,
        description: "Frontier model pricing wars, OpenAI Dots background avatars, and the race for on-device local models.",
        cover_image: "/magazine_previews/cover.webp",
        pdf_filename: "The_Neural_Feed_Illustrated_Issue_06.pdf"
    },
    {
        issue: 5,
        title: "The Neural Feed Illustrated — Issue #05",
        period_label: "28 September – 4 October 2026",
        page_count: 10,
        description: "Nvidia OpenShell agent safety stacks, Grok 4.7 compute benchmarks, and the 48-hour model drop blitz.",
        cover_image: "/magazine_previews/page_04.webp",
        pdf_filename: "The_Neural_Feed_Illustrated_Issue_05.pdf"
    }
];

export default function PastIssuesPage() {
    const navigate = useNavigate();
    const [issues, setIssues] = useState(FALLBACK_ISSUES);
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [subStatus, setSubStatus] = useState('idle');
    const [subMessage, setSubMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [subscriberToken] = useState(() => localStorage.getItem('tnf_subscriber_token') || '');
    const [isSubscribed, setIsSubscribed] = useState(() => Boolean(localStorage.getItem('tnf_subscribed')) || Boolean(localStorage.getItem('tnf_subscriber_token')));

    useEffect(() => {
        document.title = "Past Issues & Comic Archive | The Neural Feed Illustrated";
        window.scrollTo(0, 0);

        setLoading(true);
        axios.get(`${API}/magazines?type=toons&limit=20`)
            .then(res => {
                if (Array.isArray(res.data?.magazines) && res.data.magazines.length > 0) {
                    setIssues(res.data.magazines);
                }
            })
            .catch(err => {
                console.log('Using default past issues list:', err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const handleSubscribe = async (e) => {
        e.preventDefault();
        const cleanEmail = (email || '').trim().toLowerCase();
        if (!cleanEmail || !cleanEmail.includes('@')) {
            setSubStatus('error');
            setSubMessage('Please enter a valid email address.');
            return;
        }

        setSubmitting(true);
        setSubStatus('idle');
        try {
            await axios.post(`${API}/subscribers`, { email: cleanEmail });
            setSubStatus('success');
            setIsSubscribed(true);
            localStorage.setItem('tnf_subscribed', 'true');
            setSubMessage("🎉 You're in! Check your inbox for this week's issue.");
        } catch (err) {
            const msg = err.response?.data?.detail || 'Subscription failed. Please try again.';
            if (msg.toLowerCase().includes('already')) {
                setSubStatus('success');
                setIsSubscribed(true);
                localStorage.setItem('tnf_subscribed', 'true');
                setSubMessage("You're already subscribed! Future issues arrive every Monday.");
            } else {
                setSubStatus('error');
                setSubMessage(msg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#070B14] text-slate-100 flex flex-col justify-between selection:bg-amber-500/30 selection:text-amber-200">
            {/* Top Navigation */}
            <header className="border-b border-slate-800/80 bg-[#0B1120]/90 backdrop-blur-md sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3 group">
                        <img
                            src="/logo192.png"
                            alt="The Neural Feed"
                            className="w-9 h-9 rounded-lg object-cover border border-emerald-500/30 shadow-sm transition-transform group-hover:scale-105"
                        />
                        <div className="flex flex-col">
                            <span className="font-black tracking-tight text-base text-white group-hover:text-amber-400 transition-colors">
                                THE NEURAL FEED
                            </span>
                            <span className="text-[10px] font-mono tracking-wider text-slate-400 uppercase -mt-0.5">
                                Illustrated Archive
                            </span>
                        </div>
                    </Link>

                    <div className="flex items-center gap-3">
                        <Link
                            to="/subscribe"
                            className="hidden sm:inline-flex items-center text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors"
                        >
                            Get Free Weekly Issue →
                        </Link>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/')}
                            className="text-slate-300 hover:text-white hover:bg-slate-800/60 rounded-full text-xs font-semibold"
                        >
                            Back to News <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 max-w-5xl mx-auto px-4 py-10 md:py-14 w-full">
                {/* Hero Header */}
                <div className="text-center max-w-2xl mx-auto mb-10">
                    <Badge variant="outline" className="mb-4 py-1 px-3 border-amber-500/40 bg-amber-500/10 text-amber-300 rounded-full text-xs font-mono font-semibold tracking-wide">
                        <Sparkles className="w-3.5 h-3.5 mr-1.5 inline text-amber-400" />
                        WEEKLY AI COMIC CHRONICLES
                    </Badge>
                    <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white leading-tight mb-4 font-['Chivo',sans-serif]">
                        Magazine <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-200">Past Issues.</span>
                    </h1>
                    <p className="text-base text-slate-300 leading-relaxed max-w-xl mx-auto">
                        Explore every edition of The Neural Feed Illustrated. 11-page printable chronicles combining authentic tech news with retro newspaper satire.
                    </p>
                </div>

                {/* Inline Subscription Banner */}
                <Card className="border border-amber-500/30 bg-gradient-to-r from-[#0F172A] via-[#1E1B4B]/30 to-[#0F172A] shadow-xl rounded-2xl mb-12 overflow-hidden">
                    <CardContent className="p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="space-y-1.5 text-center md:text-left">
                            <span className="text-[10px] font-mono font-bold tracking-wider uppercase text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                                📬 FREE SUBSCRIPTION
                            </span>
                            <h2 className="text-lg sm:text-xl font-bold text-white">
                                Never miss a Monday issue.
                            </h2>
                            <p className="text-xs text-slate-300 max-w-md">
                                Fresh comics drop every Monday at 08:00 UTC, with our 5-minute news digest on weekdays. Always 100% free.
                            </p>
                        </div>

                        <form onSubmit={handleSubscribe} className="w-full md:w-auto flex flex-col sm:flex-row gap-2 shrink-0">
                            <div className="relative min-w-[240px]">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    type="email"
                                    required
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-9 h-10 text-xs bg-slate-900 border-slate-700 text-white rounded-lg focus-visible:ring-amber-500"
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={submitting}
                                className="h-10 text-xs font-bold bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg px-4"
                            >
                                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Subscribe Free"}
                            </Button>
                        </form>
                    </CardContent>
                    {subStatus === 'success' && (
                        <div className="bg-emerald-950/40 border-t border-emerald-800/60 px-6 py-2.5 text-emerald-300 text-xs flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                            <span>{subMessage}</span>
                        </div>
                    )}
                    {subStatus === 'error' && (
                        <div className="bg-red-950/40 border-t border-red-800/60 px-6 py-2.5 text-red-300 text-xs flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                            <span>{subMessage}</span>
                        </div>
                    )}
                </Card>

                {/* Issues Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                    {issues.map((issueItem, idx) => (
                        <Card 
                            key={issueItem.issue || idx}
                            className="border border-slate-800 bg-[#0B1120] hover:border-amber-500/40 transition-all duration-200 rounded-2xl overflow-hidden flex flex-col group"
                        >
                            <div className="p-6 flex flex-col justify-between flex-1 space-y-4">
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[10px] font-mono">
                                            ISSUE #{String(issueItem.issue || (idx + 1)).padStart(2, '0')}
                                        </Badge>
                                        <span className="text-[11px] font-mono text-slate-400 flex items-center gap-1">
                                            <Calendar className="w-3 h-3 text-slate-500" />
                                            {issueItem.period_label || "Weekly Edition"}
                                        </span>
                                    </div>
                                    <h3 className="text-lg font-bold text-white group-hover:text-amber-400 transition-colors leading-snug">
                                        {issueItem.title || `The Neural Feed Illustrated #${issueItem.issue}`}
                                    </h3>
                                    <p className="text-xs text-slate-300 leading-relaxed">
                                        {issueItem.description || "11-page printable weekly chronicle with retro newsprint aesthetic, 8 4-panel tech satires, and verified metrics."}
                                    </p>
                                </div>

                                <div className="pt-3 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-3">
                                    <span className="text-[11px] text-slate-400 flex items-center gap-1">
                                        <FileText className="w-3.5 h-3.5 text-amber-400/80" />
                                        {issueItem.page_count || 11} Pages • Printable PDF
                                    </span>
                                    {issueItem.is_public_sample ? (
                                        <a
                                            href={issueItem.public_download_url || `https://theneuralfeed.com/magazines/${issueItem.pdf_filename || `The_Neural_Feed_Illustrated_Issue_${String(issueItem.issue).padStart(2, '0')}.pdf`}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold transition-colors"
                                        >
                                            <Download className="w-3.5 h-3.5" /> Free sample, read it now
                                        </a>
                                    ) : subscriberToken ? (
                                        <a
                                            href={`${issueItem.public_download_url || `https://theneuralfeed.com/magazines/${issueItem.pdf_filename || `The_Neural_Feed_Illustrated_Issue_${String(issueItem.issue).padStart(2, '0')}.pdf`}`}?token=${subscriberToken}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold transition-colors"
                                        >
                                            <Download className="w-3.5 h-3.5" /> Read Issue
                                        </a>
                                    ) : isSubscribed ? (
                                        <div className="flex flex-col items-end gap-1">
                                            <Link
                                                to={`/magazine/${issueItem.id || `magazine-${issueItem.type || 'toons'}-issue-${String(issueItem.issue).padStart(2, '0')}`}`}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-amber-300 text-xs font-semibold transition-colors border border-amber-500/30"
                                            >
                                                <FileText className="w-3.5 h-3.5" /> Preview issue
                                            </Link>
                                            <span className="text-[10px] text-slate-400">Your full-issue link is in your Monday email</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <Link
                                                to={`/magazine/${issueItem.id || `magazine-${issueItem.type || 'toons'}-issue-${String(issueItem.issue).padStart(2, '0')}`}`}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition-colors border border-slate-700"
                                            >
                                                <FileText className="w-3.5 h-3.5" /> Preview issue
                                            </Link>
                                            <Link
                                                to="/subscribe"
                                                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold transition-all shadow-sm hover:shadow"
                                            >
                                                <Sparkles className="w-3.5 h-3.5" /> Get this issue free, subscribe
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>

                {/* Bottom Back Button */}
                <div className="text-center pt-4">
                    <Link
                        to="/subscribe"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 font-bold text-xs shadow-lg hover:shadow-amber-500/20 transition-all transform hover:-translate-y-0.5"
                    >
                        Subscribe for Upcoming Issues <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-slate-800/80 py-8 text-center text-xs text-slate-400 space-y-2">
                <p>© 2026 The Neural Feed. Independent AI Intelligence & Visual Comics.</p>
                <div className="flex items-center justify-center gap-4 text-slate-400">
                    <Link to="/" className="hover:text-slate-200 transition-colors">Daily News</Link>
                    <span>•</span>
                    <Link to="/subscribe" className="hover:text-slate-200 transition-colors">Subscribe</Link>
                    <span>•</span>
                    <Link to="/privacy" className="hover:text-slate-200 transition-colors">Privacy Policy</Link>
                    <span>•</span>
                    <a href="mailto:contact@theneuralfeed.com" className="hover:text-slate-200 transition-colors">Contact</a>
                </div>
            </footer>
        </div>
    );
}
